/**
 * What this deployment offers, asked once.
 *
 * `GET /methods` is anonymous and cheap, and it is the only honest source: the
 * server knows which providers are configured, and a client that hard-coded
 * three buttons would show one that fails whenever a deployment has not set up
 * Apple. Which is most of them, most of the time.
 *
 * Cached per client instance rather than per component. The sign-in screen and
 * the callback screen both want it, and asking twice on one page load is a
 * round trip for an answer that changes with a deployment.
 */

import { useEffect, useState } from "react";
import type { ApiClient } from "@hardauto/core-api";

/** The provider ids the server uses, which are the mobile client's. */
export const SOCIAL_PROVIDERS = ["social.google", "social.microsoft", "social.apple"] as const;

export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

export interface AuthMethods {
  /** Whether password sign-in is offered at all. */
  password: boolean;
  /** Whether to show the social block. False when no provider is configured. */
  socialEnabled: boolean;
  /** The providers that will actually work, in the order to show them. */
  providers: SocialProvider[];
  selfRegistrationEnabled: boolean;
}

interface MethodsPayload {
  methods?: { id: string; enabled: boolean }[];
  social_enabled?: boolean;
  self_registration_enabled?: boolean;
}

/**
 * The safe answer when discovery has not finished or has failed.
 *
 * Password on, social off. Fail-closed in the direction that matters: a
 * missing password form would lock everybody out of a working deployment,
 * while a missing social button costs one click on a different method.
 */
export const FALLBACK: AuthMethods = {
  password: true,
  socialEnabled: false,
  providers: [],
  selfRegistrationEnabled: true,
};

function parse(payload: MethodsPayload): AuthMethods {
  const enabled = new Map((payload.methods ?? []).map((m) => [m.id, m.enabled]));
  return {
    password: enabled.get("password") ?? true,
    socialEnabled: payload.social_enabled ?? false,
    providers: SOCIAL_PROVIDERS.filter((id) => enabled.get(id) === true),
    selfRegistrationEnabled: payload.self_registration_enabled ?? true,
  };
}

const cache = new WeakMap<ApiClient, Promise<AuthMethods>>();

/** Fetch the methods, at most once per client. */
export function authMethods(client: ApiClient): Promise<AuthMethods> {
  const existing = cache.get(client);
  if (existing) return existing;

  const pending = client
    .request<MethodsPayload>(client.anonymousPath("/methods"), { anonymous: true })
    .then(parse)
    .catch(() => {
      // A failure is not cached: the next render should try again rather than
      // remember that the network was unhappy once.
      cache.delete(client);
      return FALLBACK;
    });
  cache.set(client, pending);
  return pending;
}

/** The methods, for a component. `FALLBACK` until the answer arrives. */
export function useAuthMethods(client: ApiClient): AuthMethods {
  const [methods, setMethods] = useState<AuthMethods>(FALLBACK);

  useEffect(() => {
    let live = true;
    void authMethods(client).then((resolved) => {
      // Guarded, because a screen can unmount while this is in flight — a
      // sign-in that succeeds fast is exactly when that happens.
      if (live) setMethods(resolved);
    });
    return () => {
      live = false;
    };
  }, [client]);

  return methods;
}
